import _ from "lodash";
import { EVALUATION_MODE_CONFIG } from "../Konva/KonvaConfig";
import {
  CANVAS_IDS,
  KONVA_ATTR,
  KONVA_EVENTS,
  KONVA_ITEM_TYPE,
} from "../Konva/KonvaConstants";
import {
  drawEllipseEventListener,
  drawPolygonEventListener,
  drawRectangleEventListener,
  isEllipseNode,
  isImageNode,
  isPolygonNode,
  isRectangleNode,
  isTextNode,
} from "../Konva/KonvaElements";
import { getCanvasRelativeSize, listenBackEvents } from "../KonvaCommonUtils";
import {
  maintainTransformationScale,
  updateTransformerHandler,
} from "../Konva/KonvaUtils";

const cachedImage = {};

/**
 *
 * @param { stage, layer, transformer, ...callbacks }
 * @description konva callback to drag and drop action's
 * @description event listener for TEACHER CREATE mode
 */
const addTeacherCreateModeEventListener = props => {
  const { konva, stage, layer, transformer, drawMode } = props;

  const isDrawing = stage.getAttr(KONVA_ATTR.IS_DRAWING_POLYGON);

  if (isDrawing) {
    // * for drawing mode, listening events specifically shape wise
    const type = _.get(drawMode, "type", KONVA_ITEM_TYPE.RECTANGLE);
    const DRAW_EVENT_LISTENERS = {
      [KONVA_ITEM_TYPE.RECTANGLE]: drawRectangleEventListener,
      [KONVA_ITEM_TYPE.ELLIPSE]: drawEllipseEventListener,
      [KONVA_ITEM_TYPE.POLYGON]: drawPolygonEventListener,
    };
    const drawingEventListeners = _.get(DRAW_EVENT_LISTENERS, type);
    if (_.isFunction(drawingEventListeners)) drawingEventListeners(props);
    return;
  }

  // * Change cursor on mouse hover
  const onMouseOver = e => {
    const node = e.target;
    if (node instanceof konva.Shape) {
      stage.container().style.cursor = "pointer";
    } else {
      stage.container().style.cursor = "default";
    }
  };

  // * add transfomer on clicking shape
  const onClick = e => {
    const node = e.target;
    updateTransformerHandler(node, props);
  };

  // * fire ondrop event when item is dropped
  const onDragEnd = e => {
    const node = e.target;
    const payload = { id: node.id(), x: node.x(), y: node.y() };
    node.moveTo(layer);

    if (node instanceof konva.Shape) props.onDragEnd(payload);
  };

  // * maintain shape scale on transform
  const onTransform = e => {
    const node = e.target;
    if (!node) return;

    maintainTransformationScale(node, props);
  };

  // * give a callback on transform end with updated shape props
  const onTransformEnd = e => {
    const node = e?.target;
    if (!node) return;

    const isText = isTextNode(node, konva);
    const isImage = isImageNode(node, konva);
    const isEllipse = isEllipseNode(node, konva);
    const isPolygon = isPolygonNode(node, konva);
    const isRectangle = isRectangleNode(node, konva);

    const payload = {
      id: node.id(),
      x: node.x(),
      y: node.y(),
    };

    if (isRectangle || isImage) {
      _.set(payload, "width", node.width());
      _.set(payload, "height", node.height());
    } else if (isEllipse) {
      _.set(payload, "radiusX", node.radiusX());
      _.set(payload, "radiusY", node.radiusY());
    } else if (isText) {
      _.set(payload, "fontSize", node.fontSize());
    } else if (isPolygon) {
      _.set(payload, "points", node.points());
    }

    props.onTransform(payload);
  };

  listenBackEvents(stage, transformer, {
    onClick,
    onDragEnd,
    onMouseOver,
    onTransform,
    onTransformEnd,
  });

  return {};
};

/**
 *
 * @param { stage, layer, transformer, ...callbacks }
 * @description konva callback to drag and drop action's
 * @description event listener for TEACHER PREVIEW mode
 */
const addEditAndPreviewModeEventListener = (props, mode) => {
  var previousShape;
  const { konva, stage, layer, virtualLayer, transformer } = props;

  const isEditMode = mode === "edit";

  // * Change cursor on mouse hover
  const onMouseOver = e => {
    const node = e.target;
    if (node instanceof konva.Shape) {
      const name = _.isFunction(node?.name) ? node.name() : "";
      const isDraggable = _.first(_.split(name, "#")) === "DRAGGABLE";
      if (!isDraggable) return;
      stage.container().style.cursor = "grab";
    } else {
      stage.container().style.cursor = "default";
    }
  };

  // * add grabbing cursor on drag start
  const onDragStart = e => {
    const node = e.target;

    const wrapperNode = stage.findOne(
      `#${node.id()}_${CANVAS_IDS.TEXT_WRAPPER_ID}`
    );
    if (wrapperNode) {
      wrapperNode.moveTo(virtualLayer);
    }
    node.moveTo(virtualLayer);

    _.forEach(_.toArray(layer?.children), thisNode => {
      const nodeName = _.split(thisNode.name(), "#");
      if (_.first(nodeName) === "DROPZONE") {
        thisNode.moveToTop();
      }
    });

    layer.draw();
    stage.container().style.cursor = "grabbing";

    const name = _.isFunction(node?.name) ? node.name() : "";
    const isDraggable = _.first(_.split(name, "#")) === "DRAGGABLE";
    if (isDraggable) stage.setAttr(KONVA_ATTR.DRAGGED_ITEM_ID, node.id());

    if (isEditMode && _.isFunction(props?.onDrop)) {
      props.onDrop({ draggedItemId: node.id(), action: "REMOVE" });
    }
  };

  // * on drag end update position
  const onDragEnd = e => {
    if (previousShape) {
      previousShape.fire(KONVA_EVENTS.DROP, { evt: e.evt }, true);
      previousShape = undefined;
    }
    const node = e.target;
    node.moveTo(layer);
    stage.setAttr(KONVA_ATTR.DRAGGED_ITEM_ID, undefined);

    const payload = { id: node.id(), x: node.x(), y: node.y() };
    if (node instanceof konva.Shape) props.onDragEnd(payload);
  };

  // * when draggable element enter inside dropzone
  const onDragEnter = e => {
    const node = e.target;
    const draggedItemId = stage.getAttr(KONVA_ATTR.DRAGGED_ITEM_ID);
    const name = _.isFunction(node?.name) ? node.name() : "";
    const isDropzone = _.first(_.split(name, "#")) === "DROPZONE";

    if (!draggedItemId || !isDropzone) return;

    node.fill("rgba(0, 181, 204, 0.4)");
  };

  // * reset shape after dragged item leave
  const onDragLeave = e => {
    const node = e.target;
    const name = _.isFunction(node?.name) ? node.name() : "";
    const isDropzone = _.first(_.split(name, "#")) === "DROPZONE";
    if (!isDropzone) return;

    node.fill("transparent");
  };

  // * if dropped item is dropzone, callback onDrop
  const onDrop = e => {
    const node = e.target;
    const name = _.isFunction(node?.name) ? node.name() : "";
    const isDropzone = _.first(_.split(name, "#")) === "DROPZONE";
    if (isDropzone) node.fill("transparent");

    const draggedItemId = stage.getAttr(KONVA_ATTR.DRAGGED_ITEM_ID);
    if (isEditMode && _.isFunction(props?.onDrop) && draggedItemId) {
      props.onDrop({ draggedItemId, dropzoneId: node.id(), action: "ADD" });
    }
  };

  // * fire needed events on drag move
  const onDragMove = e => {
    const draggedElementId = stage.getAttr(KONVA_ATTR.DRAGGED_ITEM_ID);
    if (!draggedElementId) return;

    const draggedElement = stage.findOne(`#${draggedElementId}`);
    if (!draggedElement) return;

    // * Getting mid point of currently dragged element and firing event on dragzone interaction
    const xCoord = draggedElement.x() + draggedElement.width() / 2;
    const yCoord = draggedElement.y() + draggedElement.height() / 2;

    if (stage && (stage.width() === 0 || stage.height() === 0)) return;
    const shape = layer.getIntersection({ x: xCoord, y: yCoord });

    if (previousShape && shape) {
      if (previousShape !== shape) {
        previousShape.fire(KONVA_EVENTS.DRAGLEAVE, { evt: e.evt }, true);
        shape.fire(KONVA_EVENTS.DRAGENTER, { evt: e.evt }, true);
        previousShape = shape;
      } else {
        previousShape.fire(KONVA_EVENTS.DRAGOVER, { evt: e.evt }, true);
      }
    } else if (!previousShape && shape) {
      previousShape = shape;
      shape.fire(KONVA_EVENTS.DRAGENTER, { evt: e.evt }, true);
    } else if (previousShape && !shape) {
      previousShape.fire(KONVA_EVENTS.DRAGLEAVE, { evt: e.evt }, true);
      previousShape = undefined;
    }
  };

  listenBackEvents(stage, transformer, {
    onDrop,
    onDragEnd,
    onDragMove,
    onDragStart,
    onMouseOver,
    onDragEnter,
    onDragLeave,
  });
};

/**
 *
 * @param {*} konvaInstance
 * @param {*} mode
 * @param {*} cb
 * @description Event listener for drag and drop question
 */
export const addDragAndDropQuestionEventListener = (
  konvaInstance = {},
  mode = "",
  cb = () => {}
) => {
  if (mode === "create") {
    addTeacherCreateModeEventListener(konvaInstance);
    cb();
  } else if (mode === "edit" || mode === "preview") {
    addEditAndPreviewModeEventListener(konvaInstance, mode);
    cb();
  }
};

/**
 *
 * @param {*} konva instance
 * @description action after
 */
export const showSolutionForSubmittedQuestion = (
  mode,
  { konva, stage, layer, options = {} }
) => {
  if (mode !== "view") return;

  // * if user viewing solution, no wrapper needed
  if (
    _.get(options, "viewSolution", false) ||
    !_.get(options, "isSubmitted", false)
  )
    return;

  const elements = layer.getChildren();
  let correctAnswers = [];

  // * Getting list of correct answers
  const answersMapping = stage.getAttr("answersMapping");
  _.forEach(elements, element => {
    const name = element.name();
    const isDropzone = _.first(_.split(name, "#")) === "DROPZONE";
    if (!isDropzone) return;

    const correctElements =
      element?.getAttr(KONVA_ATTR.DROPZONE_TAGGED_ANSWER) || [];
    if (correctElements && _.isArray(correctElements)) {
      const dropzoneId = element.id();
      const userAnswers = _.get(answersMapping, dropzoneId, []) || [];

      correctAnswers = _.uniq([
        ...correctAnswers,
        ..._.intersection(userAnswers, correctElements),
      ]);
    }
  });

  // * Updating draggable items with answer wrapper
  _.forEach(elements, element => {
    const name = element.name();
    const isDraggable = _.first(_.split(name, "#")) === "DRAGGABLE";
    if (!isDraggable) return;

    let tickPosition = { x: 0, y: 0 };
    const isCorrect = _.includes(correctAnswers, element.id());
    if (element instanceof konva.Image) {
      /**
       * @IMAGE Evaluation status for draggable `Image` Node
       */
      element.stroke(
        !isCorrect
          ? EVALUATION_MODE_CONFIG.WRONG_ANSWER_STROKE
          : EVALUATION_MODE_CONFIG.CORRECT_ANSWER_STROKE
      );
      element.strokeWidth(
        getCanvasRelativeSize(EVALUATION_MODE_CONFIG.IMAGE_STROKE_WIDTH)
      );

      tickPosition = {
        x: element.x() + element.width(),
        y: element.y(),
      };
    } else if (element instanceof konva.Text) {
      /**
       * @TEXT Evaluation status for draggable `Text` Node
       */
      const id = element.id();
      const wrapperNode = stage.findOne(`#${id}_${CANVAS_IDS.TEXT_WRAPPER_ID}`);

      if (wrapperNode) {
        wrapperNode.fill(
          !isCorrect
            ? EVALUATION_MODE_CONFIG.WRONG_ANSWER_FILL
            : EVALUATION_MODE_CONFIG.CORRECT_ANSWER_FILL
        );
        wrapperNode.stroke(
          !isCorrect
            ? EVALUATION_MODE_CONFIG.WRONG_ANSWER_STROKE
            : EVALUATION_MODE_CONFIG.CORRECT_ANSWER_STROKE
        );
        wrapperNode.strokeWidth(
          getCanvasRelativeSize(EVALUATION_MODE_CONFIG.TEXT_STROKE_WIDTH)
        );
        element.text(_.trim(element.text()));
        element.fontStyle(600);
        element.fill(
          !isCorrect
            ? EVALUATION_MODE_CONFIG.WRONG_ANSWER_STROKE
            : EVALUATION_MODE_CONFIG.CORRECT_ANSWER_STROKE
        );

        wrapperNode.width(element.width());
      }

      tickPosition = {
        x: element.x() + element.width(),
        y: element.y(),
      };
    }

    if (tickPosition.x > 0 || tickPosition.y > 0) {
      const imageUrl = isCorrect
        ? EVALUATION_MODE_CONFIG.CORRECT_ANSWER_SRC
        : EVALUATION_MODE_CONFIG.WRONG_ANSWER_SRC;

      const iconSize = getCanvasRelativeSize(16);
      const imageNode = new konva.Image({
        x: tickPosition.x - iconSize / 2,
        y: tickPosition.y - iconSize / 2,
        width: iconSize,
        height: iconSize,
        draggable: false,
        listening: false,
      });
      layer.add(imageNode);

      // * Using cached image if available
      if (cachedImage[imageUrl]) {
        imageNode.image(cachedImage[imageUrl]);
        return;
      }

      const img = new Image();
      img.src = imageUrl;
      img.onload = function () {
        cachedImage[imageUrl] = this;
        imageNode.image(this);
      };
    }
  });
};
